import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import Badge from 'react-bootstrap/Badge'
import Button from 'react-bootstrap/Button'
import ButtonGroup from 'react-bootstrap/ButtonGroup'
import Card from 'react-bootstrap/Card'
import Collapse from 'react-bootstrap/Collapse'
import Form from 'react-bootstrap/Form'
import Table from 'react-bootstrap/Table'
import {
  holeDisagreements,
  isEdited,
  layoutDraft,
  losersOf,
  memberById,
  survivorIdOf,
} from './layoutMergeDraft'

export const REASON_LABELS = {
  ONLY_PLAYED: 'only played',
  MOST_PLAYED: 'most played',
  NEWEST: 'newest',
}

export const day = (timestamp) => (timestamp ? new Date(timestamp).toISOString().slice(0, 10) : '—')

const snippet = (text) => {
  if (!text) return <span className="text-muted">—</span>
  return text.length > 60 ? <span title={text}>{text.slice(0, 60)}…</span> : text
}

const FIELDS = [
  { key: 'name', label: 'Name', rows: 1 },
  { key: 'info', label: 'Description', rows: 2 },
  { key: 'info2', label: 'Directions', rows: 2 },
]

// Picking the survivor's name, description and directions out of the group. Each field can be
// taken from any member or typed, the same idea as the course survivor editor with far fewer fields.
function LayoutSurvivorEditor({ group, draft, onChange }) {
  const set = (key, value) => onChange({ ...draft, [key]: value })
  return (
    <div className="mt-2">
      {FIELDS.map((field) => {
        const options = group.members.filter((m) => m[field.key])
        return (
          <Form.Group key={field.key} className="mb-2">
            <Form.Label className="small mb-1">{field.label}</Form.Label>
            <div className="d-flex gap-2">
              <Form.Select
                size="sm"
                style={{ maxWidth: '12rem' }}
                aria-label={`Take ${field.label.toLowerCase()} from`}
                value=""
                onChange={(e) => {
                  const member = memberById(group, Number(e.target.value))
                  if (member) set(field.key, member[field.key])
                }}
              >
                <option value="">Take from…</option>
                {options.map((m) => (
                  <option key={m.layoutId} value={m.layoutId}>
                    #{m.layoutId} {field.key === 'name' ? '' : m.name}
                  </option>
                ))}
              </Form.Select>
              <Form.Control
                size="sm"
                as={field.rows > 1 ? 'textarea' : 'input'}
                rows={field.rows > 1 ? field.rows : undefined}
                aria-label={`Survivor ${field.label.toLowerCase()}`}
                value={draft[field.key] ?? ''}
                onChange={(e) => set(field.key, e.target.value === '' ? null : e.target.value)}
              />
            </div>
          </Form.Group>
        )
      })}
      <Form.Check
        type="checkbox"
        id={`combine-${group.groupKey}`}
        label="Combine hole data — fill the survivor's blank hole names, distances, tees, baskets and routes from the others"
        checked={!!draft.combineHoles}
        onChange={(e) => set('combineHoles', e.target.checked)}
      />
    </div>
  )
}

// Hole by hole, one row per layout, with the holes the layouts disagree about highlighted. Pars
// always agree -- that is what put them in one group -- so it is distances and names that show.
function HoleComparison({ members }) {
  const distances = holeDisagreements(members, 'distance')
  const names = holeDisagreements(members, 'name')
  return (
    <div className="table-responsive">
      <Table size="sm" bordered className="small mb-0 layout-merge-holes">
        <thead>
          <tr>
            <th>Layout</th>
            {distances.map((_, i) => (
              <th key={i} className="text-center">
                {i + 1}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {members.map((m) => (
            <tr key={m.layoutId}>
              <td className="text-nowrap">#{m.layoutId}</td>
              {m.holes.map((hole, i) => (
                <td
                  key={i}
                  className={`text-center${distances[i] || names[i] ? ' table-warning' : ''}`}
                  title={[hole.name, hole.info].filter(Boolean).join(' — ') || undefined}
                >
                  <div>{hole.distance ? Math.round(hole.distance) : '—'}</div>
                  {hole.name && <div className="text-muted">{hole.name}</div>}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  )
}

// One group of same-shape layouts that differ somewhere a human has to look.
//
// Unlike a course merge, nothing is accepted by default: every group here reached review
// *because* the layouts are not identical, so merging one is a decision, not a confirmation.
export default function LayoutMergeGroupCard({
  group,
  choice,
  onChoice,
  onPreview,
  onMerge,
  previewed,
  applying,
}) {
  const [holesOpen, setHolesOpen] = useState(false)
  const [editorOpen, setEditorOpen] = useState(!!choice?.draft)

  const accepted = !!choice?.accepted
  const survivorId = survivorIdOf(group, choice)
  const survivor = memberById(group, survivorId)
  const excluded = new Set(choice?.excluded || [])
  const draft = choice?.draft || layoutDraft(survivor)
  const losers = losersOf(group, choice)
  const edited = isEdited(choice?.draft, survivor)

  const update = (patch) => onChoice({ ...(choice || {}), ...patch })

  // A draft is picked relative to one survivor. Switching survivor starts it again.
  const pickSurvivor = (layoutId) =>
    update({
      survivorId: layoutId,
      draft: undefined,
      excluded: (choice?.excluded || []).filter((id) => id !== layoutId),
    })

  const toggleIncluded = (layoutId, include) =>
    update({
      excluded: include
        ? (choice?.excluded || []).filter((id) => id !== layoutId)
        : [...(choice?.excluded || []), layoutId],
    })

  return (
    <Card className={`mb-3 curation-layout-merge${accepted ? '' : ' curation-merge-skipped'}`}>
      <Card.Header className="d-flex flex-wrap align-items-center gap-2">
        <strong>
          <Link to={`/course/${group.courseId}`}>{group.courseName || `Course ${group.courseId}`}</Link>
        </strong>
        <span className="text-muted">
          {group.holeCount} holes, par {group.par}
        </span>
        <Badge bg="secondary">{group.members.length} layouts</Badge>
        {group.pendingAutoGroupKeys.length > 0 && (
          <Badge bg="info" text="dark" title="Apply the automatic merges first, or in the same batch">
            {group.pendingAutoGroupKeys.length} identical{' '}
            {group.pendingAutoGroupKeys.length === 1 ? 'copy set' : 'copy sets'} merged automatically first
          </Badge>
        )}
        {edited && <Badge bg="warning" text="dark">survivor edited</Badge>}
      </Card.Header>

      <Table size="sm" responsive className="mb-0 align-middle">
        <thead>
          <tr>
            <th title="The layout that stays visible">Keep</th>
            <th title="Untick to leave a layout out of this merge">Hide</th>
            <th>Id</th>
            <th>Name</th>
            <th>Description</th>
            <th>Directions</th>
            <th title="Active games / the layout.played counter">Games</th>
            <th>Updated</th>
            <th>Differs by</th>
          </tr>
        </thead>
        <tbody>
          {group.members.map((m) => {
            const isSurvivor = m.layoutId === survivorId
            return (
              <tr key={m.layoutId} className={isSurvivor ? 'table-success' : undefined}>
                <td>
                  <Form.Check
                    type="radio"
                    name={`survivor-${group.groupKey}`}
                    aria-label={`Keep ${m.layoutId}`}
                    checked={isSurvivor}
                    onChange={() => pickSurvivor(m.layoutId)}
                  />
                </td>
                <td>
                  <Form.Check
                    type="checkbox"
                    aria-label={`Hide ${m.layoutId}`}
                    disabled={isSurvivor}
                    checked={!isSurvivor && !excluded.has(m.layoutId)}
                    onChange={(e) => toggleIncluded(m.layoutId, e.target.checked)}
                  />
                </td>
                <td>
                  <Link to={`/course/${group.courseId}/${m.layoutId}`}>{m.layoutId}</Link>
                  {m.version > 1 && <span className="text-muted small ms-1">v{m.version}</span>}
                </td>
                <td>{m.name}</td>
                <td className="small">{snippet(m.info)}</td>
                <td className="small">{snippet(m.info2)}</td>
                <td>
                  <strong>{m.games}</strong>
                  <span className="text-muted small"> / {m.playedCounter}</span>
                </td>
                <td className="text-nowrap small">{day(m.updated)}</td>
                <td>
                  {m.layoutId === group.survivorId && survivorId === group.survivorId ? (
                    <Badge bg="success">{REASON_LABELS[group.survivorReason] || group.survivorReason}</Badge>
                  ) : (
                    m.differences.map((d) => (
                      <Badge key={d} bg="light" text="dark" className="me-1 border">
                        {d}
                      </Badge>
                    ))
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </Table>

      <Card.Body className="py-2">
        <div className="d-flex gap-2 flex-wrap">
          <Button size="sm" variant="link" className="p-0" onClick={() => setHolesOpen((o) => !o)}>
            {holesOpen ? 'Hide holes' : 'Compare holes'}
          </Button>
          <Button size="sm" variant="link" className="p-0" onClick={() => setEditorOpen((o) => !o)}>
            {editorOpen ? 'Close the survivor editor' : 'Edit the surviving layout'}
          </Button>
        </div>
        <Collapse in={holesOpen}>
          <div>{holesOpen && <HoleComparison members={group.members} />}</div>
        </Collapse>
        <Collapse in={editorOpen}>
          <div>
            {editorOpen && (
              <>
                <LayoutSurvivorEditor
                  group={group}
                  draft={draft}
                  onChange={(next) => update({ draft: next })}
                />
                {edited && (
                  <Button size="sm" variant="link" className="p-0" onClick={() => update({ draft: undefined })}>
                    Reset to #{survivorId} as it is
                  </Button>
                )}
              </>
            )}
          </div>
        </Collapse>
      </Card.Body>

      <Card.Footer className="d-flex flex-wrap gap-2 align-items-center">
        <ButtonGroup size="sm">
          <Button
            variant={accepted ? 'success' : 'outline-success'}
            onClick={() => update({ accepted: true })}
            disabled={losers.length === 0}
          >
            Merge
          </Button>
          <Button variant={accepted ? 'outline-secondary' : 'secondary'} onClick={() => update({ accepted: false })}>
            Skip
          </Button>
        </ButtonGroup>
        <span className="text-muted small">
          {losers.length === 0
            ? 'nothing to hide'
            : `hides ${losers.map((m) => `#${m.layoutId}`).join(', ')} behind #${survivorId}`}
        </span>
        <span className="ms-auto d-flex gap-2">
          <Button size="sm" variant="outline-secondary" onClick={onPreview} disabled={applying || losers.length === 0}>
            Preview this group
          </Button>
          <Button
            size="sm"
            variant="danger"
            onClick={onMerge}
            disabled={applying || !previewed || losers.length === 0}
            title={previewed ? undefined : 'Preview this group first'}
          >
            Merge this group now — cannot be undone
          </Button>
        </span>
      </Card.Footer>
    </Card>
  )
}
